import React from 'react';
import { connect } from 'react-redux';
import { Box, Button, Typography } from '@drone-js/core';
import { mapDispatchToProps, mapStateToProps, mergeProps } from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCardBottomSection/OfferCardFooter.selectors';
import { Props } from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCardBottomSection/OfferCardFooter.types';
import { Price } from 'component/common/Price/Price';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesAvailableOptionCardChooseButtonClickEventActivitiesFieldsV6,
    ActivitiesAvailableOptionCardFooterDetailClickEventActivitiesFieldsV5,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesAvailableOptionCardChooseButtonClickEventActivitiesManifestV6,
    ActivitiesAvailableOptionCardFooterDetailClickEventActivitiesManifestV5,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { getFirstAvailableEnrichedOfferOption } from 'component/page/detail/PackageOptions/PackageOptionsUtils';
import {
    getEnrichedOfferIdFromQueryParams,
    getOfferWithMinPricingSummary,
    getPaxFromQueryParams,
    mapPromocodeBadgesFromPricingSummary,
    mapTotalPriceByOffer,
} from 'gql/service/graph/activityService';
import OfferCardContainer from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardContainer/OfferCardContainer';
import { killEvent } from 'core/utils/domUtils';
import { ActivityOfferToken } from 'core/type/Details.types';
import { roundNumberWithDecimal } from 'core/utils/roundNumberWithDecimal';
import ActivityCardBadge from 'component/common/Badge/ActivityCardBadge';
import { ActivityBadgeCategory } from 'component/common/Badge/BadgeService.types';
import { useLocation } from 'react-router-dom';
import { mapPaxFromQueryParams } from 'core/utils/getPax';
import { Offer } from 'gql/autogen/Offer';

// eslint-disable-next-line @agoda/agoda-a11y/missing-keyboard-tests -- @agoda/agoda-a11y/missing-keyboard-tests[c85b9f21-15bb-433e-801b-3d916d64fa9c]
const OfferCardFooter: React.FC<Props> = (props) => {
    const {
        offer,
        cms,
        detail,
        selectedDate,
        priceBehavior,
        offerIndex,
        isSelectedOffer,
        isAvailLoading,
        isUnavailableOffer,
        navigationId,
        activityTokenFromState,
        setOffer,
        onSelectOffer,
        setBookingRequirementById,
        setIsDisplayWarning,
        isDisplayWarning,
        showInclusiveTax,
        passengers,
        setPassengers,
        shouldExpandOffer,
        getCalendarPrice,
        isExpandFirstOfferCardInitiated,
        setIsExpandFirstOfferCardInitiated,
        experiments,
        maintainSelectedOfferOnDetails,
        setShowContinueYourBookingNotification,
        continueYourBooking,
        isFixedWrongOfferOption,
        selectedFirstOffer,
    } = props;
    const locationSearch = useLocation().search;
    const autoSelectedOfferOptions = continueYourBooking || maintainSelectedOfferOnDetails;
    const enrichedOfferIdFromQueryParams = getEnrichedOfferIdFromQueryParams(locationSearch);
    const enrichedOfferOptions = offer.enrichedOfferOption || [];
    const offerDetailsReference = enrichedOfferOptions[0]?.offerDetailsReference;
    const offerTitle = detail.offerDetails?.find((d) => d.offerDetailRef === offerDetailsReference)?.title || '';
    const replaceOfferIndex = enrichedOfferOptions.some((enrichedOfferOption) => enrichedOfferOption.offerId === enrichedOfferIdFromQueryParams);
    const expandFirstCard =
        selectedFirstOffer || (!autoSelectedOfferOptions && props.offerIndex === 0 && props.expandFirstOfferCardOnDetailsLanding && !isExpandFirstOfferCardInitiated);
    const paxParams = getPaxFromQueryParams(locationSearch);
    const [isFirstLanding, setIsFirstLanding] = React.useState<boolean>(true);

    React.useEffect(() => {
        if (isSelectedOffer) {
            setPassengers(passengers);
        }
    }, [isSelectedOffer, passengers, setPassengers]);

    const useBookingRequirement = (selectedOffer?: Offer) => {
        return React.useCallback(() => {
            const offerOption = isFixedWrongOfferOption && selectedOffer ? selectedOffer : offer;
            const enrichedOfferOption = offerOption.enrichedOfferOption || [];
            const firstAvailableOfferOption = getFirstAvailableEnrichedOfferOption(enrichedOfferOption);
            const firstEnrichedOfferOption = enrichedOfferOption[0];
            const offerToken: ActivityOfferToken = firstEnrichedOfferOption?.offerOptionToken
                ? { activityToken: firstEnrichedOfferOption.offerOptionToken, enableOfferFilter: true }
                : { activityToken: activityTokenFromState, enableOfferFilter: false };

            setBookingRequirementById(firstAvailableOfferOption?.bookingRequirementRefId ?? firstEnrichedOfferOption?.bookingRequirementRefId ?? -1);
            onSelectOffer(offerToken, `#${navigationId}`, !expandFirstCard);
            if (expandFirstCard) setIsExpandFirstOfferCardInitiated(true);
        }, [selectedOffer]);
    };

    const usePassengerLogic = () => {
        return React.useCallback(() => {
            const paxFormat = paxParams && mapPaxFromQueryParams(paxParams);
            const hasUnknownPaxType = paxFormat && paxFormat.some((pax) => pax.code === 'UNKNOWN');
            if (!hasUnknownPaxType) {
                paxFormat && setPassengers(paxFormat);
            }
        }, []);
    };

    const setBookingRequirement = useBookingRequirement(isFixedWrongOfferOption ? offer : undefined);
    const setPaxFromQueryParam = usePassengerLogic();

    const setSelectedOffer = React.useCallback(() => {
        setOffer({ offer, offerIndex });
        setBookingRequirement();
    }, [offer, offerIndex, setBookingRequirement, setOffer]);

    React.useEffect(() => {
        if (isFirstLanding && replaceOfferIndex && (continueYourBooking || maintainSelectedOfferOnDetails)) {
            !offer.isAvailable && continueYourBooking && setShowContinueYourBookingNotification(cms[371433]);
            setOffer({ offer, offerIndex });
            setPaxFromQueryParam();
            setBookingRequirement();
            setIsFirstLanding(false);
        }
    }, [
        cms,
        continueYourBooking,
        isFirstLanding,
        maintainSelectedOfferOnDetails,
        offer,
        offerIndex,
        replaceOfferIndex,
        setBookingRequirement,
        setOffer,
        setPaxFromQueryParam,
        setShowContinueYourBookingNotification,
    ]);

    React.useEffect(() => {
        if (shouldExpandOffer || expandFirstCard) {
            setSelectedOffer();
        }
    }, [expandFirstCard, setSelectedOffer, shouldExpandOffer]);

    const pricingSummary = getOfferWithMinPricingSummary(offer.enrichedOfferOption || [])?.pricingSummary;
    const onOptionSelected = (e: React.MouseEvent) => {
        killEvent(e);
        setSelectedOffer();
        const offerPrice = mapTotalPriceByOffer(pricingSummary);
        const calendarPrice = getCalendarPrice(enrichedOfferOptions);
        trackElementEvent<ActivitiesAvailableOptionCardChooseButtonClickEventActivitiesFieldsV6>(
            ActivitiesAvailableOptionCardChooseButtonClickEventActivitiesManifestV6.ids,
            {
                activities_is_unavailable_offer: isUnavailableOffer || false,
                travel_date: getDateInYmdFormat(selectedDate),
                final_price: offerPrice,
                // Need to round due to IEEE 754 floating point arithmetic
                price_difference: roundNumberWithDecimal(offerPrice - calendarPrice, 2),
            },
            {
                action_element_index: offerIndex + 1,
                offer_id: offer.offerId,
                activity_id: offer.activityId,
            },
        );
    };

    const isOptionDisabled = React.useMemo(() => {
        const enrichedOfferOption = offer.enrichedOfferOption || [];
        const availableEnrichedOfferOption = enrichedOfferOption.filter((option) => option.isAvailable);
        return isUnavailableOffer || availableEnrichedOfferOption.length === 0;
    }, [offer.enrichedOfferOption, isUnavailableOffer]);

    const onFooterDetailClick = () => {
        trackElementEvent<ActivitiesAvailableOptionCardFooterDetailClickEventActivitiesFieldsV5>(
            ActivitiesAvailableOptionCardFooterDetailClickEventActivitiesManifestV5.ids,
            {
                activities_is_unavailable_offer: isUnavailableOffer || false,
            },
            {
                action_element_index: offerIndex + 1,
                activity_id: offer.activityId,
            },
        );
    };

    if (isSelectedOffer) {
        return <OfferCardContainer isDisplayWarning={isDisplayWarning} setIsDisplayWarning={setIsDisplayWarning} offerIndex={offerIndex} />;
    }

    const promocodeBadges = mapPromocodeBadgesFromPricingSummary(pricingSummary, experiments);

    return (
        <div>
            {!isOptionDisabled && !isAvailLoading && promocodeBadges.length > 0 && (
                <Box display="flex" pb="4" data-testid="activity-promocodes-badges-container">
                    <ActivityCardBadge badges={promocodeBadges} activityId={offer.activityId || 0} badgeCategory={ActivityBadgeCategory.PROMOCODE} isBVariant={true} />
                </Box>
            )}
            <Box data-testid="available-option-card-footer" display="flex" flexDirection="row" flexWrap="nowrap" justifyContent="space-between">
                <Box display="flex" flexDirection="column" justifyContent="flex-end">
                    {/* eslint-disable-next-line @agoda/agoda-a11y/no-incorrect-props, @agoda/agoda-a11y/no-incorrect-drone-box-usage -- @agoda/agoda-a11y/no-incorrect-props[fc466779-382f-4fd8-9182-54bcb69188c1], @agoda/agoda-a11y/no-incorrect-drone-box-usage[386095d9-2699-4de5-9d97-451d48220e8c] */}
                    <Box
                        display="flex"
                        flexDirection="column"
                        data-testid="activities-available-option-card-footer-detail"
                        data-element-index={offerIndex + 1}
                        alignSelf={!isOptionDisabled ? 'flex-start' : 'center'}
                        onClick={onFooterDetailClick}
                    >
                        {!isOptionDisabled && (
                            <>
                                <div data-testid="available-option-card-footer-price">
                                    <Price
                                        pricingSummary={pricingSummary}
                                        isDisplayPricePerPax={false}
                                        priceDisplay={priceBehavior.displayType}
                                        priceDirection="column"
                                        finalPriceSize="l"
                                        isMobileScreen={props.screenSize.mobile}
                                    />
                                </div>
                                {showInclusiveTax && (
                                    <Typography variant="caption-default" textColor="base.subtle">
                                        {cms[319651]}
                                    </Typography>
                                )}
                            </>
                        )}

                        {isOptionDisabled && (
                            <div data-testid="no-availability-offer-label">
                                <Typography variant="caption-default" textColor="base.subtle">
                                    {cms[225580]}
                                </Typography>
                            </div>
                        )}
                    </Box>
                </Box>
                <Box display="flex" flexDirection="column" justifyContent="flex-end" alignSelf="center">
                    <Button
                        variant="label"
                        state={isAvailLoading ? 'disabled' : 'enabled'}
                        width="parent-fit"
                        hierarchy="secondary"
                        onClick={onOptionSelected}
                        data-element-index={offerIndex + 1}
                        data-element-name="activities-available-option-card-choose-button"
                        data-testid="activities-available-option-card-choose-button"
                        label={isOptionDisabled ? cms[304916] : cms[126909]}
                        aria-label={`${isOptionDisabled ? cms[225580] : cms[126909]} ${offerTitle}`}
                    />
                </Box>
            </Box>
        </div>
    );
};
OfferCardFooter.displayName = 'OfferCardBottomSection';

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(OfferCardFooter);
